/**
 * Approvals Inbox — a dedicated panel showing pending approvals with quick
 * Approve / Reject / Return-for-changes actions. Linked from the top-bar
 * ApprovalsBell → /app/approvals/inbox.
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { CheckCircle2, XCircle, RotateCcw, Inbox, RefreshCw } from "lucide-react";

export default function ApprovalsInbox() {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openReject, setOpenReject] = useState(null);
  const [remark, setRemark] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/approvals-v2/inbox");
      setPending(r.data.pending || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load inbox");
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const act = async (row, verb) => {
    try {
      await api.post(`/approvals-v2/${row.id}/${verb}`,
        { remark: remark || undefined });
      toast.success(`Voucher ${verb}d`);
      setOpenReject(null); setRemark("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${verb} failed`);
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="approvals-inbox-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Inbox className="w-8 h-8 text-emerald-500" />
            Approvals Inbox
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Pending vouchers awaiting your approval · {pending.length} item{pending.length !== 1 && "s"}
          </p>
        </div>
        <Button variant="secondary" onClick={load} disabled={loading}
                  data-testid="btn-refresh">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground text-center py-12" data-testid="inbox-loading">
          Loading pending approvals…
        </div>
      ) : pending.length === 0 ? (
        <div className="text-center py-16 border rounded" data-testid="inbox-empty">
          <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-500 mb-3" />
          <div className="font-medium">All clear · nothing pending your approval</div>
          <div className="text-xs text-muted-foreground mt-1">You'll see items here once colleagues submit vouchers over your threshold.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {pending.map((row) => (
            <Card key={row.id} data-testid={`approval-card-${row.id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <CardTitle className="text-base">
                      {row.entity?.toUpperCase() || "Voucher"} · {row.entity_id?.slice(0, 12) || "—"}
                    </CardTitle>
                    <div className="text-xs text-muted-foreground mt-1">
                      Submitted by {row.submitted_by || "—"}
                      {row.submitted_at && ` · ${new Date(row.submitted_at).toLocaleString()}`}
                    </div>
                  </div>
                  <Badge className="whitespace-nowrap">₹{Number(row.amount || 0).toLocaleString("en-IN")}</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {row.description && (
                  <div className="text-sm">{row.description}</div>
                )}
                {row.rule_matched && (
                  <div className="text-xs text-muted-foreground">
                    Rule: {row.rule_matched}
                  </div>
                )}
                {openReject === row.id ? (
                  <div className="space-y-2">
                    <Textarea placeholder="Reason for rejection or return-for-changes"
                                value={remark} onChange={(e) => setRemark(e.target.value)}
                                data-testid={`textarea-reject-${row.id}`} />
                    <div className="flex gap-2">
                      <Button size="sm" variant="destructive"
                                onClick={() => act(row, "reject")}
                                data-testid={`btn-confirm-reject-${row.id}`}>
                        <XCircle className="w-4 h-4 mr-1" /> Confirm Reject
                      </Button>
                      <Button size="sm" variant="secondary"
                                onClick={() => act(row, "return")}
                                data-testid={`btn-confirm-return-${row.id}`}>
                        <RotateCcw className="w-4 h-4 mr-1" /> Return for Changes
                      </Button>
                      <Button size="sm" variant="ghost"
                                onClick={() => { setOpenReject(null); setRemark(""); }}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => act(row, "approve")}
                              className="bg-emerald-600 hover:bg-emerald-700"
                              data-testid={`btn-approve-${row.id}`}>
                      <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                    </Button>
                    <Button size="sm" variant="outline"
                              onClick={() => setOpenReject(row.id)}
                              data-testid={`btn-reject-${row.id}`}>
                      Reject / Return
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
